import { events } from "@constants/tracking";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { getNormalizedScore } from "@features/session/helpers/aiFeedback";
import { useAppTheme } from "@hooks/useAppTheme";
import { useColorsFromMastery } from "@hooks/useColorsFromMasteryLevels";
import { TTSContainer } from "@memorang/features/src/TTS";
import { Box, DonutChart, OverlineText, TextSkeleton } from "@memorang/ui";
import { trackCustomEvent } from "analytics";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { useState } from "react";
import { Platform, ScrollView, type ViewStyle } from "react-native";
import { List, Text } from "react-native-paper";

import { AIFeedbackMenu } from "@features/session/components/AIFeedbackMenu";
import { type ReflectSkillMeta, useScenarioBundle } from "../data/scenarios";
import type {
	ProethicsScenarioId,
	SummativeCriterion,
	SummativeResult,
} from "../types";

// Streamed results arrive partial (score/reason can land after name), so a
// criterion's score is undefined until it streams in.
const getCriterionPoints = (criterion?: Partial<SummativeCriterion>) =>
	typeof criterion?.score === "number" ? criterion.score : undefined;

const SummativeCriterionRow = ({
	meta,
	criterion,
}: {
	meta: ReflectSkillMeta;
	criterion?: Partial<SummativeCriterion>;
}) => {
	const theme = useAppTheme();
	const colors = useColorsFromMastery();
	const [expanded, setExpanded] = useState(false);

	const points = getCriterionPoints(criterion);
	const chipLabel = `${points ?? "--"} / ${meta.max}`;

	const getChipColor = () => {
		if (points === undefined) {
			return {
				backgroundColor: theme.colors.progress.none,
				color: theme.colors.text,
			};
		}
		const normalizedScore = getNormalizedScore(points, meta.max);
		if (normalizedScore < 40) {
			return { backgroundColor: colors.basic, color: "white" };
		}
		if (normalizedScore < 60) {
			return { backgroundColor: colors.lowIntermediate, color: "white" };
		}
		if (normalizedScore < 80) {
			return { backgroundColor: colors.highIntermediate, color: "white" };
		}
		return { backgroundColor: colors.advanced, color: "white" };
	};

	const chipColor = getChipColor();

	return (
		<List.Accordion
			title={meta.name}
			titleNumberOfLines={3}
			titleStyle={theme.fonts.bodyLarge}
			expanded={expanded}
			onPress={() => setExpanded((prev) => !prev)}
			style={{
				backgroundColor: theme.colors.surface,
				paddingHorizontal: 16,
				paddingVertical: 4,
			}}
			left={({ style }) => (
				<Box
					style={[style, { marginLeft: 0, alignSelf: "center" }]}
					justifyContent="center"
				>
					<Icon name={meta.icon} size={18} color={theme.colors.text} />
				</Box>
			)}
			right={({ isExpanded }) => (
				<Box
					flexDirection="row"
					alignItems="center"
					gap={8}
					// cancels Paper's fixed marginVertical:6 on the right slot to top-align with the title
					marginTop={-6}
					// matches the Feedback row's IconButton overhang so glyph centers line up
					marginRight={-12}
				>
					{/* Display-only badge: avoid Paper's Chip here since its <button>
					    would nest inside the Accordion header's <button>. */}
					<Box
						style={{
							backgroundColor: chipColor.backgroundColor,
							borderRadius: 8,
							paddingHorizontal: 10,
							paddingVertical: 4,
						}}
					>
						<Text variant="labelSmall" style={{ color: chipColor.color }}>
							{chipLabel}
						</Text>
					</Box>
					<Box width={34} alignItems="center">
						<Icon
							name={isExpanded ? "chevron-up" : "chevron-down"}
							size={24}
							color={theme.colors.onSurfaceVariant}
						/>
					</Box>
				</Box>
			)}
		>
			<Box paddingLeft={16 + 34} paddingRight={16} paddingBottom={8}>
				{criterion?.reason ? (
					<CustomMarkdownRenderer
						text={criterion.reason}
						variant="explanation"
						fontSize={14}
					/>
				) : (
					<TextSkeleton numLines={3} height={16} />
				)}
			</Box>
		</List.Accordion>
	);
};

export const ResultsScreen = ({
	result,
	scenarioId,
}: {
	result: Partial<SummativeResult>;
	scenarioId: ProethicsScenarioId;
}) => {
	const theme = useAppTheme();
	const reflectSkills = useScenarioBundle(scenarioId).reflectSkills;
	const totalMax = reflectSkills.reduce((sum, meta) => sum + meta.max, 0);

	const criteria = result.criteria ?? [];
	const criterionPoints = criteria
		.map(getCriterionPoints)
		.filter((points): points is number => points !== undefined);
	const earned = criterionPoints.reduce((sum, points) => sum + points, 0);
	const hasScore = criterionPoints.length > 0;
	const feedbackStreaming = !result.feedback;

	return (
		<ScrollView
			style={[
				{ flex: 1, backgroundColor: theme.colors.surface },
				Platform.OS === "web" &&
					({ scrollbarGutter: "stable" } as unknown as ViewStyle),
			]}
			contentContainerStyle={{
				paddingVertical: 16,
				maxWidth: 760,
				alignSelf: "center",
				width: "100%",
			}}
		>
			<Box gap={16} marginTop={16}>
				<Box alignItems="center" justifyContent="center">
					<DonutChart
						skeleton={!hasScore}
						distribution={[earned, totalMax - earned]}
						innerRadius={52}
						variant="progress"
						radius={64}
						score={hasScore ? (earned / totalMax) * 100 : undefined}
					>
						<Box alignItems="center" justifyContent="center">
							<Text variant="titleLarge">
								{hasScore ? `${earned} / ${totalMax}` : `-- / ${totalMax}`}
							</Text>
							<OverlineText>Overall</OverlineText>
						</Box>
					</DonutChart>
				</Box>
				<Box>
					{reflectSkills.map((meta, index) => (
						<SummativeCriterionRow
							key={meta.name}
							meta={meta}
							criterion={criteria[index]}
						/>
					))}
				</Box>
				<Box paddingHorizontal={16}>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
					>
						<CustomListSubheaderContainer
							enableGutters={false}
							style={{ paddingVertical: 16 }}
							textVariant="titleMedium"
						>
							Feedback
						</CustomListSubheaderContainer>
						<Box
							flexDirection="row"
							alignItems="center"
							gap={6}
							marginRight={-12}
						>
							<TTSContainer
								explanation={result.feedback}
								eventName={events.ttsClicked}
								trackCustomEvent={trackCustomEvent}
								itemId={scenarioId}
								size={18}
								disabled={feedbackStreaming}
							/>
							<AIFeedbackMenu
								itemId={scenarioId}
								featureName="AI Feedback"
								disabled={feedbackStreaming}
							/>
						</Box>
					</Box>
					{result.feedback ? (
						<CustomMarkdownRenderer
							text={result.feedback}
							variant="explanation"
							fontSize={16}
							customStyles={{
								paragraph: {
									lineHeight: 22,
									marginBottom: 8,
								},
							}}
							paragraphMarginVertical={6}
						/>
					) : (
						<TextSkeleton numLines={4} height={20} />
					)}
				</Box>
			</Box>
		</ScrollView>
	);
};
